import { number_, RatedApiResponse, RatedApiSensor } from "../jefdaqLegacyApi/rated";

import { VolumeFlowRateUnits } from "convert-units/definitions/volumeFlowRate";
import { IDischargeData_, ISample_, StreamSensor_ } from "../models/StreamSensor";
import notEmpty from "../shared/notEmpty";
import { streamPeriods, streamPeriodSeconds } from "../shared/streamPeriods";

// Handles 'rated' API, but filtered to just discharge, hence 'stream'
export const ratedApiToSensors = (
  ratedApiResponse: RatedApiResponse
): StreamSensor_[] => {
  const { periodsSeconds, timezone } = ratedApiResponse.meta;

  // rated encodes 'latest' as 0 second period
  const expectedPeriodsSeconds = [0].concat(streamPeriodSeconds);
  if (
    JSON.stringify(periodsSeconds) !== JSON.stringify(expectedPeriodsSeconds)
  ) {
    throw new Error("Stage periods do not match");
  }
  const type = "Stage-Discharge";
  const dischargeIdx = ratingTypeIdx(ratedApiResponse, type);
  if (dischargeIdx === undefined) {
    return [];
  }

  interface RatedUnit {
    unit: VolumeFlowRateUnits;
    index: number; // in the array of units in API response
    idx: number; // normRatingUnits in JEFDAQ
  }
  const coerceApiUnit = (
    apiUnit: string,
    unit: VolumeFlowRateUnits,
    discharges: [string | null, ...number_[]][][]
  ): RatedUnit | undefined => {
    const index = ratingUnitIndex(ratedApiResponse, type, apiUnit);
    if (index === undefined) {
      return undefined;
    }
    const idx = ratingUnitIdx(ratedApiResponse, type, index);
    if (idx !== undefined && discharges[index].length > 0) {
      return { unit, index, idx };
    }
    return undefined;
  };

  const apiSensorToStreamSensor = (
    apiSensor: RatedApiSensor
  ): StreamSensor_ | undefined => {
    const type = "stream"; // since rated is always stage while doing discharge
    const { site, name, source, latitude, longitude } = apiSensor;
    const allDischarges = apiSensor.rateds[dischargeIdx];

    const cfs = coerceApiUnit("cfs", "ft3/s", allDischarges);
    const gpm = coerceApiUnit("gpm", "gal/min", allDischarges);
    const ratedUnit = cfs || gpm;

    if (ratedUnit === undefined) {
      console.error("No compatible unit for discharge sensor " + site);
      return undefined;
    }

    const unit = ratedUnit.unit;
    const dischargesForUnit = allDischarges[ratedUnit.index];

    const [latestDischarge, ...discharges] = dischargesForUnit;
    if (discharges.length !== streamPeriodSeconds.length) {
      // https://github.com/JEFuller/jefdaq/issues/305
      //console.error(
      //  "Can't match values with periods for discharge sensor" + idx
      //);
      return undefined;
    }

    // https://github.com/JEFuller/jefdaq/blob/c930c0896485f1624537606c955b9b04bf5d6dc3/Core/sensorData.py#L470-L472
    // https://github.com/JEFuller/jefdaq/blob/c930c0896485f1624537606c955b9b04bf5d6dc3/Utilities/ratedValues.py#L430
    // https://github.com/JEFuller/jefdaq/blob/c930c0896485f1624537606c955b9b04bf5d6dc3/Utilities/ratedValues.py#L341-L342
    const [time, ...values] = latestDischarge;
    const value = values[ratedUnit.idx]
    if (time == null || value == null) {
      return undefined;
    }
    const latest = { time, value };

    const max: { [period: string]: ISample_ } = {}
    for (let i = 0; i < streamPeriodSeconds.length; i++) {
      const [time, ...values] = discharges[i];
      const value = values[ratedUnit.idx]

      if (time && value) {
        const period = streamPeriods[i];
        if (value) {
          max[period] = { time, value };
        }
      }
    }

    const discharge: IDischargeData_ = { unit, latest, max };

    const sensor: StreamSensor_ = {
      type,
      id: site,   // not a typo, inconsistent
      site: name, // naming with legacy API
      timezone,
      latitude,
      longitude,
      owner: source,
      stage: undefined,
      discharge
    };

    return sensor;
  };

  return ratedApiResponse.data.map(apiSensorToStreamSensor).filter(notEmpty);
};

const ratingTypeIdx = (
  ratedApiResponse: RatedApiResponse | undefined,
  type: string
): number | undefined => {
  if (ratedApiResponse) {
    const {
      meta: { ratingTypes },
    } = ratedApiResponse;
    const idx = ratingTypes.indexOf(type);
    return idx >= 0 ? idx : undefined;
  } else {
    return undefined;
  }
};

const ratingUnitIndex = (
  ratedApiResponse: RatedApiResponse | undefined,
  type: string,
  unit: string
): number | undefined => {
  const typeIdx = ratingTypeIdx(ratedApiResponse, type);
  if (typeIdx !== undefined) {
    if (ratedApiResponse) {
      const {
        meta: { ratingUnits },
      } = ratedApiResponse;
      const idx = ratingUnits[typeIdx].indexOf(unit);
      return idx >= 0 ? idx : undefined;
    } else {
      return undefined;
    }
  }
};

export const ratingUnitIdx = (
  ratedApiResponse: RatedApiResponse | undefined,
  type: string,
  ratingUnitIndex: number
): number | undefined => {
  const typeIdx = ratingTypeIdx(ratedApiResponse, type);
  if (typeIdx !== undefined) {
    if (ratedApiResponse) {
      const {
        meta: { ratingUnitIdxs },
      } = ratedApiResponse;
      const idx = ratingUnitIdxs[typeIdx][ratingUnitIndex];
      return idx >= 0 ? idx : undefined;
    } else {
      return undefined;
    }
  }
};
